import { useCallback, useEffect, useState } from 'react';

export interface PostalCodeFields {
  cities: readonly string[];
  cantons: readonly string[];
}

type PostalCodeOptions = Record<string, PostalCodeFields>;
type CityPostalCodeIndex = Map<string, { zips: Set<string>; cantons: Set<string> }>;
type CantonPostalCodeIndex = Map<string, { zips: Set<string>; cities: Set<string> }>;

interface UsePostalCodeReferenceOptions {
  enabled?: boolean;
}

export function usePostalCodeReference(options?: UsePostalCodeReferenceOptions) {
  const enabled = options?.enabled ?? true;

  const [postalCodeOptions, setPostalCodeOptions] = useState<PostalCodeOptions | null>(null);
  const [allZipOptions, setAllZipOptions] = useState<string[]>([]);
  const [allCityOptions, setAllCityOptions] = useState<string[]>([]);
  const [allCantonOptions, setAllCantonOptions] = useState<string[]>([]);
  const [byCity, setByCity] = useState<CityPostalCodeIndex>(() => new Map());
  const [byCanton, setByCanton] = useState<CantonPostalCodeIndex>(() => new Map());
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!enabled || postalCodeOptions) return;

    let isMounted = true;

    const loadPostalCodeOptions = async () => {
      try {
        setIsLoading(true);

        const mod = await import('../data/postalCodeOptions');
        const loadedPostalCodeOptions = mod.postalCodeOptions as PostalCodeOptions;
        const loadedByCity: CityPostalCodeIndex = new Map();
        const loadedByCanton: CantonPostalCodeIndex = new Map();
        const loadedCityOptions = new Set<string>();
        const loadedCantonOptions = new Set<string>();

        Object.entries(loadedPostalCodeOptions).forEach(([zip, entry]) => {
          entry.cities.forEach((city) => {
            loadedCityOptions.add(city);

            const cityIndex = loadedByCity.get(city) ?? {
              zips: new Set<string>(),
              cantons: new Set<string>(),
            };

            cityIndex.zips.add(zip);
            entry.cantons.forEach((canton) => cityIndex.cantons.add(canton));
            loadedByCity.set(city, cityIndex);
          });

          entry.cantons.forEach((canton) => {
            loadedCantonOptions.add(canton);

            const cantonIndex = loadedByCanton.get(canton) ?? {
              zips: new Set<string>(),
              cities: new Set<string>(),
            };

            cantonIndex.zips.add(zip);
            entry.cities.forEach((city) => cantonIndex.cities.add(city));
            loadedByCanton.set(canton, cantonIndex);
          });
        });

        if (!isMounted) return;

        setPostalCodeOptions(loadedPostalCodeOptions);
        setAllZipOptions(Object.keys(loadedPostalCodeOptions).sort((a, b) => Number(a) - Number(b)));
        setAllCityOptions(Array.from(loadedCityOptions).sort((a, b) => a.localeCompare(b)));
        setAllCantonOptions(Array.from(loadedCantonOptions).sort((a, b) => a.localeCompare(b)));
        setByCity(loadedByCity);
        setByCanton(loadedByCanton);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void loadPostalCodeOptions();

    return () => {
      isMounted = false;
    };
  }, [enabled, postalCodeOptions]);

  const getEntryByZip = useCallback(
    (zip: string): PostalCodeFields | undefined => {
      const normalizedZip = zip?.trim() ?? '';
      if (!postalCodeOptions || !normalizedZip) return undefined;
      return postalCodeOptions[normalizedZip];
    },
    [postalCodeOptions],
  );

  return {
    allZipOptions,
    allCityOptions,
    allCantonOptions,
    byCity,
    byCanton,
    getEntryByZip,
    isLoading,
  };
}
